import { useCallback, useMemo, useState } from 'react';
import type { JSX, MutableRefObject } from 'react';
import { createPortal } from 'react-dom';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  LexicalTypeaheadMenuPlugin,
  MenuOption,
  useBasicTypeaheadTriggerMatch,
} from '@lexical/react/LexicalTypeaheadMenuPlugin';
import {
  $createParagraphNode,
  $getSelection,
  $isRangeSelection,
  type ElementNode,
  type LexicalEditor,
  type TextNode,
} from 'lexical';
import { $setBlocksType } from '@lexical/selection';
import { $createHeadingNode, $createQuoteNode } from '@lexical/rich-text';
import { $createCodeNode } from '@lexical/code';
import {
  INSERT_CHECK_LIST_COMMAND,
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
} from '@lexical/list';
import { INSERT_TABLE_COMMAND } from '@lexical/table';
import { INSERT_HORIZONTAL_RULE_COMMAND } from '@lexical/react/LexicalHorizontalRuleNode';
import { useGT } from 'gt-react';
import {
  ParagraphIcon,
  TextHOneIcon,
  TextHTwoIcon,
  TextHThreeIcon,
  ListBulletsIcon,
  ListNumbersIcon,
  ListChecksIcon,
  QuotesIcon,
  CodeBlockIcon,
  TableIcon,
  MinusIcon,
} from '@phosphor-icons/react';

/**
 * Notion-style "/" block menu. Typing `/` at a block opens a filterable list of
 * block types; selecting one removes the typed query and applies the block to
 * the current selection. Reuses Lexical's typeahead plumbing, the same
 * mechanism behind the @mention picker.
 *
 * Only blocks that round-trip to markdown are offered, so nothing inserted here
 * is lost on save.
 */
class SlashOption extends MenuOption {
  title: string;
  keywords: string[];
  icon: JSX.Element;
  onSelect: (editor: LexicalEditor) => void;

  constructor(
    title: string,
    options: {
      keywords?: string[];
      icon: JSX.Element;
      onSelect: (editor: LexicalEditor) => void;
    },
  ) {
    super(title);
    this.title = title;
    this.keywords = options.keywords ?? [];
    this.icon = options.icon;
    this.onSelect = options.onSelect;
  }
}

function $setBlock(editor: LexicalEditor, createNode: () => ElementNode) {
  editor.update(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      $setBlocksType(selection, createNode);
    }
  });
}

/**
 * Which optional blocks the slash menu may insert. Mirrors the toolbar's flags
 * so a caller that hides (say) tables in the toolbar can't have them re-enabled
 * through `/`. Omitted flags default to enabled.
 */
export interface SlashFeatureFlags {
  checklist?: boolean;
  codeBlock?: boolean;
  table?: boolean;
  horizontalRule?: boolean;
}

function buildOptions(
  gt: ReturnType<typeof useGT>,
  flags: SlashFeatureFlags,
): SlashOption[] {
  return [
    new SlashOption(gt('Text', { context: 'slash menu: paragraph' }), {
      keywords: ['paragraph', 'text', 'body'],
      icon: <ParagraphIcon className="h-4 w-4" />,
      onSelect: (editor) => $setBlock(editor, () => $createParagraphNode()),
    }),
    new SlashOption(gt('Heading 1', { context: 'slash menu: h1' }), {
      keywords: ['h1', 'title', 'heading'],
      icon: <TextHOneIcon className="h-4 w-4" />,
      onSelect: (editor) => $setBlock(editor, () => $createHeadingNode('h1')),
    }),
    new SlashOption(gt('Heading 2', { context: 'slash menu: h2' }), {
      keywords: ['h2', 'subtitle', 'heading'],
      icon: <TextHTwoIcon className="h-4 w-4" />,
      onSelect: (editor) => $setBlock(editor, () => $createHeadingNode('h2')),
    }),
    new SlashOption(gt('Heading 3', { context: 'slash menu: h3' }), {
      keywords: ['h3', 'heading'],
      icon: <TextHThreeIcon className="h-4 w-4" />,
      onSelect: (editor) => $setBlock(editor, () => $createHeadingNode('h3')),
    }),
    new SlashOption(gt('Bullet list', { context: 'slash menu: bullet list' }), {
      keywords: ['unordered', 'bullet', 'list', 'ul'],
      icon: <ListBulletsIcon className="h-4 w-4" />,
      onSelect: (editor) =>
        editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined),
    }),
    new SlashOption(
      gt('Numbered list', { context: 'slash menu: numbered list' }),
      {
        keywords: ['ordered', 'numbered', 'list', 'ol'],
        icon: <ListNumbersIcon className="h-4 w-4" />,
        onSelect: (editor) =>
          editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined),
      },
    ),
    ...(flags.checklist !== false
      ? [
          new SlashOption(
            gt('Check list', { context: 'slash menu: check list' }),
            {
              keywords: ['todo', 'task', 'check', 'checkbox'],
              icon: <ListChecksIcon className="h-4 w-4" />,
              onSelect: (editor) =>
                editor.dispatchCommand(INSERT_CHECK_LIST_COMMAND, undefined),
            },
          ),
        ]
      : []),
    new SlashOption(gt('Quote', { context: 'slash menu: quote' }), {
      keywords: ['blockquote', 'quote', 'citation'],
      icon: <QuotesIcon className="h-4 w-4" />,
      onSelect: (editor) => $setBlock(editor, () => $createQuoteNode()),
    }),
    ...(flags.codeBlock !== false
      ? [
          new SlashOption(
            gt('Code block', { context: 'slash menu: code block' }),
            {
              keywords: ['code', 'snippet', 'pre'],
              icon: <CodeBlockIcon className="h-4 w-4" />,
              onSelect: (editor) => $setBlock(editor, () => $createCodeNode()),
            },
          ),
        ]
      : []),
    ...(flags.table !== false
      ? [
          new SlashOption(gt('Table', { context: 'slash menu: table' }), {
            keywords: ['table', 'grid', 'rows', 'columns'],
            icon: <TableIcon className="h-4 w-4" />,
            onSelect: (editor) =>
              editor.dispatchCommand(INSERT_TABLE_COMMAND, {
                columns: '3',
                rows: '3',
                includeHeaders: true,
              }),
          }),
        ]
      : []),
    ...(flags.horizontalRule !== false
      ? [
          new SlashOption(
            gt('Divider', { context: 'slash menu: horizontal rule' }),
            {
              keywords: ['divider', 'horizontal', 'rule', 'separator', 'hr'],
              icon: <MinusIcon className="h-4 w-4" />,
              onSelect: (editor) =>
                editor.dispatchCommand(
                  INSERT_HORIZONTAL_RULE_COMMAND,
                  undefined,
                ),
            },
          ),
        ]
      : []),
  ];
}

function SlashMenuItem({
  option,
  isSelected,
  onSelect,
  onMouseEnter,
}: {
  option: SlashOption;
  isSelected: boolean;
  onSelect: () => void;
  onMouseEnter: () => void;
}): JSX.Element {
  return (
    <li
      ref={(el) => option.setRefElement(el)}
      role="option"
      aria-selected={isSelected}
      className={`flex items-center gap-2 px-3 py-1.5 cursor-pointer select-none rounded ${
        isSelected
          ? 'bg-accent text-accent-foreground'
          : 'text-popover-foreground hover:bg-accent'
      }`}
      onMouseDown={(event) => {
        event.preventDefault();
        onSelect();
      }}
      onMouseEnter={onMouseEnter}
    >
      <span className="text-muted-foreground">{option.icon}</span>
      <span className="text-sm">{option.title}</span>
    </li>
  );
}

export function SlashCommandPlugin({
  features = {},
}: {
  features?: SlashFeatureFlags;
} = {}): JSX.Element {
  const [editor] = useLexicalComposerContext();
  const gt = useGT();
  const [query, setQuery] = useState('');

  const { checklist, codeBlock, table, horizontalRule } = features;
  const allOptions = useMemo(
    () => buildOptions(gt, { checklist, codeBlock, table, horizontalRule }),
    [gt, checklist, codeBlock, table, horizontalRule],
  );

  const triggerFn = useBasicTypeaheadTriggerMatch('/', {
    minLength: 0,
    maxLength: 30,
  });

  const options = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return allOptions;
    return allOptions.filter(
      (option) =>
        option.title.toLowerCase().includes(q) ||
        option.keywords.some((keyword) => keyword.includes(q)),
    );
  }, [allOptions, query]);

  const onQueryChange = useCallback((matchingString: string | null) => {
    setQuery(matchingString ?? '');
  }, []);

  const onSelectOption = useCallback(
    (
      selectedOption: SlashOption,
      nodeToRemove: TextNode | null,
      closeMenu: () => void,
    ) => {
      // Drop the typed "/query" first, then apply the block to the cleared line.
      editor.update(() => {
        nodeToRemove?.remove();
      });
      selectedOption.onSelect(editor);
      closeMenu();
    },
    [editor],
  );

  const menuRenderFn = useCallback(
    (
      anchorElementRef: MutableRefObject<HTMLElement | null>,
      {
        selectedIndex,
        selectOptionAndCleanUp,
        setHighlightedIndex,
        options: renderedOptions,
      }: {
        selectedIndex: number | null;
        selectOptionAndCleanUp: (option: SlashOption) => void;
        setHighlightedIndex: (index: number) => void;
        options: SlashOption[];
      },
    ) => {
      if (!anchorElementRef.current || renderedOptions.length === 0)
        return null;
      return createPortal(
        <div
          className="z-50 w-56 max-h-72 overflow-y-auto rounded-md border border-border bg-popover text-popover-foreground shadow-md py-1"
          role="listbox"
        >
          <ul>
            {renderedOptions.map((option, index) => (
              <SlashMenuItem
                key={option.key}
                option={option}
                isSelected={selectedIndex === index}
                onSelect={() => selectOptionAndCleanUp(option)}
                onMouseEnter={() => setHighlightedIndex(index)}
              />
            ))}
          </ul>
        </div>,
        anchorElementRef.current,
      );
    },
    [],
  );

  return (
    <LexicalTypeaheadMenuPlugin<SlashOption>
      options={options}
      onQueryChange={onQueryChange}
      onSelectOption={onSelectOption}
      triggerFn={triggerFn}
      menuRenderFn={menuRenderFn}
    />
  );
}
